








































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ConfirmationModal, EventType, MAX_ATTACHMENTS, UploadStatus } from '@/constants';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import UploadIcon from '@/assets/icons/UploadIcon.vue';
import InfoIcon from '@/assets/icons/InfoIcon.vue';
import { hideLoader, showLoader, validateFileformatAndSize } from '@/util';
import { Attachment, AttachmentUrls, ConfirmationModalData, Photos } from '@/types';
import { generateUniqueId } from '../services/service';
import { deletePhoto, uploadPhoto } from '@/api';

export default Vue.extend({
  name: 'AttachmentsModal',
  components: {
    Attachment: () => import('./Attachment.vue'),
    RemoveAttachmentModal: () => import('@/components/modals/ConfirmationModal.vue'),
    CloseIcon,
    UploadIcon,
    InfoIcon
  },
  props: {
    activeConversation: {
      type: String,
      required: false
    },
    isLoggedInAsAdmin: {
      type: Boolean,
      required: true
    },
    myPhotos: {
      type: Boolean,
      required: false
    },
    jobId: {
      type: String || null,
      required: false
    }
  },
  data () {
    return {
      attachments: [] as Attachment[],
      uploadedFiles: [] as AttachmentUrls[],
      limitExceeded: false,
      isVisibleConfirmationModal: false,
      modalData: {} as ConfirmationModalData,
      selectedFileForRemoval: {} as any,
      EventType
    }
  },
  mounted () {
    this.$parent.$on('resetAttachmentsData', this.clearData);
  },
  computed: {
    ...mapGetters(['selectedJob', 'user']),
    activeAttachments (): Attachment[] {
      return this.attachments.filter(attachment => attachment.status !== UploadStatus.REMOVED);
    },
    disableDone (): boolean {
      const filesUploading = this.activeAttachments.filter(attachment => !attachment.status);
      return filesUploading.length === this.activeAttachments.length;
    },
    disableCancel (): boolean {
      const filesUploading = this.activeAttachments.find(attachment => !attachment.status);
      return !!filesUploading;
    }
  },
  methods: {
    preventDefault (event: Event) {
      event.preventDefault();
      event.stopPropagation();
    },
    handleDrop (e: any) {
      this.preventDefault(e);
      this.addFilesToList(e.dataTransfer.files);
    },
    addFilesToList (files: File[]) {
      let prevIdx = this.attachments.length ? this.attachments[this.attachments.length - 1].id : '';
      for (const file of Array.from(files)) {
        if (this.activeAttachments.length < MAX_ATTACHMENTS) {
          const message = validateFileformatAndSize(file, this.activeConversation);
          const attachment = {
            id: generateUniqueId(10).toString(),
            file: file,
            error: message,
            prev: prevIdx,
            status: message ? UploadStatus.UPLOAD_FAILED : ''
          } as Attachment;
          this.attachments.push(attachment);
          prevIdx = attachment.id;
        } else {
          this.limitExceeded = true;
          break;
        }
      }
    },
    handleFileUpload (e: any) {
      this.addFilesToList(e.target.files);
    },
    handleFileRemoveEvent (data: any) {
      this.selectedFileForRemoval = data;
      if (data.status === UploadStatus.COMPLETED && !this.isLoggedInAsAdmin) {
        this.isVisibleConfirmationModal = true;
        this.modalData = ConfirmationModal.RemoveAttachment;
      } else {
        this.removeAttachmentFromList();
      }
    },
    async deleteUploadedAttachment () {
      this.removeFile();
      this.closeConfirmationModal();
    },
    removeAttachmentFromList (id?: string) {
      if (id) {
        this.selectedFileForRemoval.id = id;
      }
      const attachmentIdx = this.attachments.findIndex(attachment => attachment.id === this.selectedFileForRemoval.id);
      if (attachmentIdx > -1) {
        this.attachments[attachmentIdx].status = UploadStatus.REMOVED;
        this.limitExceeded = false;
      }
    },
    async removeFile () {
      const fileId = this.selectedFileForRemoval.id;
      const uploadedFileIdx = this.uploadedFiles.findIndex(file => file.id === this.selectedFileForRemoval.id);
      this.uploadedFiles.splice(uploadedFileIdx, 1);
      this.removeAttachmentFromList();
      if (this.activeConversation) {
        this.$emit(EventType.DELETE_ATTACHMENT, {
          fileName: this.selectedFileForRemoval.fileName,
          id: this.selectedFileForRemoval.id
        });
      } else {
        showLoader();
        await deletePhoto({
          jobId: this.jobId,
          collectionId: fileId
        });
        hideLoader();
      }
    },
    fileUploaded (data: AttachmentUrls) {
      const idx = this.attachments.findIndex(attachment => attachment.id === data.id);
      this.attachments[idx].status = UploadStatus.COMPLETED;
      this.uploadedFiles.push(data);
    },
    uploadFailed (data: AttachmentUrls) {
      const idx = this.attachments.findIndex(attachment => attachment.id === data.id);
      this.attachments[idx].status = UploadStatus.UPLOAD_FAILED;
    },
    uploadInfected (data: AttachmentUrls) {
      const idx = this.attachments.findIndex(attachment => attachment.id === data.id);
      this.attachments[idx].status = UploadStatus.INFECTED;
    },
    updateImage (data: Attachment) {
      if (data.photoDescription) {
        const requiredAttachment = this.attachments.find(attachment => attachment.id === data.id);
        requiredAttachment ? requiredAttachment.photoDescription = data.photoDescription : console.log('no attachment found');
      }
    },
    async setAttachmentData () {
      if (this.disableCancel) {
        return;
      }
      this.clearFailedFiles();
      if (this.activeConversation) {
        this.$emit(EventType.ATTACHMENT_UPLOADED, this.uploadedFiles);
      } else {
        await this.updateFiles();
        this.$emit(EventType.UPLOAD_IMAGES);
      }
    },
    closeConfirmationModal () {
      this.modalData = {} as ConfirmationModalData;
      this.isVisibleConfirmationModal = false;
      this.selectedFileForRemoval = {};
    },
    clearFailedFiles () {
      const failedAttachments = this.attachments.filter(attachment => attachment.error || attachment.status === UploadStatus.REMOVED);
      failedAttachments.forEach(failedAttachment => {
        const idx = this.attachments.findIndex(attachment => attachment.id === failedAttachment.id);
        this.attachments.splice(idx, 1);
      });
      this.limitExceeded = false;
    },
    clearData () {
      this.attachments = [];
      this.uploadedFiles = [];
      this.selectedFileForRemoval = {};
      this.limitExceeded = false;
    },
    resetFileUploader () {
      (this.$refs.fileUploader as HTMLInputElement).value = '';
    },
    async updateFiles () {
      showLoader();
      for (const attachment of this.attachments) {
        if (attachment.photoDescription) {
          const attachmentUrl = this.uploadedFiles.find(file => file.id === attachment.id);
          await uploadPhoto({
            collectionId: attachment.id,
            altTag: `${attachment.photoDescription ?? ''}`,
            contentType: 'image',
            uploadedByEmailId: this.user.email,
            uploadedByType: 'BUYER',
            uploadedById: this.user.contactId,
            assetValue: attachmentUrl?.url,
            caption: attachment.photoDescription ?? '',
            name: attachment.file.name,
            categoryTypeId: '0',
            application: '',
            order: '0',
            jobId: this.jobId,
            isNewRecord: false
          } as Photos);
        }
      }
      hideLoader();
      this.clearData();
    }
  }
});
